"use client";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSurplusSales, deleteSurplusSale, reset } from "@/state/surplusSales/surplusSales.slice";
import { fetchUsers } from "@/state/users/users.slice";
import { fetchBusiness } from "@/state/business/business.slice";
import { getShops } from "@/state/shops/shop.slice";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass,
  Plus,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import PermissionGuard from "@/components/custom/common/PermissionGuard";
import { User, Store, Loader2, RefreshCcw, Pencil, Trash2, DollarSign, TrendingUp } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { usePermission } from "@/hooks/usePermission";
import { numberWithCommas } from "@/lib/formatCommas";
import SurplusSalesDialog from "@/components/custom/sales/SurplusSalesDialog";

export default function SurplusSalesPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { surplusSales, loading, pagination, summary, success, error } = useSelector((state) => state.surplusSales);
  const { business } = useSelector((state) => state.business || {});
  const { shops } = useSelector((state) => state.shop || { shops: [] });
  const { users } = useSelector((state) => state.users || { users: [] });
  const currentUser = useSelector((state) => state.auth.user);

  const [shopFilter, setShopFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // Dialog states
  const [surplusSalesDialogOpen, setSurplusSalesDialogOpen] = useState(false);
  const [editingSurplusSale, setEditingSurplusSale] = useState(null);
  const [deleteSurplusDialogOpen, setDeleteSurplusDialogOpen] = useState(false);
  const [surplusToDelete, setSurplusToDelete] = useState(null);

  // Permissions
  const canManageSurplusSales = usePermission('ManageSurplusSales');
  const canViewAllSales = usePermission('ViewAllSales');

  // Filter shops based on user permissions
  const viewableShops = canViewAllSales
    ? shops
    : shops?.filter(shop => {
        const userShopId = currentUser?.shop_id;
        const allowedShops = currentUser?.allowed_shops || [];
        return shop.id === userShopId || allowedShops.includes(shop.id);
      }) || [];

  useEffect(() => {
    dispatch(fetchBusiness());
    dispatch(getShops());
    dispatch(fetchUsers());
    dispatch(fetchSurplusSales({ page, limit, startDate, endDate, shopId: shopFilter || undefined, userId: userFilter || undefined }));
  }, [dispatch, page, limit, startDate, endDate, shopFilter, userFilter]);

  useEffect(() => {
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error });
      dispatch(reset());
    }
    if (success) {
      dispatch(reset());
      dispatch(fetchSurplusSales({ page, limit, startDate, endDate, shopId: shopFilter || undefined, userId: userFilter || undefined }));
    }
  }, [error, success, toast, dispatch, page, limit, startDate, endDate, shopFilter, userFilter]);

  const handleRefresh = () => {
    dispatch(fetchSurplusSales({ page, limit, startDate, endDate, shopId: shopFilter || undefined, userId: userFilter || undefined }));
  };

  const handleAddSurplusSale = () => {
    setEditingSurplusSale(null);
    setSurplusSalesDialogOpen(true);
  };

  const handleEditSurplusSale = (surplusSale) => {
    setEditingSurplusSale(surplusSale);
    setSurplusSalesDialogOpen(true);
  };

  const handleDeleteSurplusClick = (surplusSale) => {
    setSurplusToDelete(surplusSale);
    setDeleteSurplusDialogOpen(true);
  };

  const handleConfirmDeleteSurplus = async () => {
    if (!surplusToDelete) return;
    try {
      await dispatch(deleteSurplusSale(surplusToDelete.id)).unwrap();
      toast({ title: "Success", description: "Surplus sale deleted successfully" });
      setDeleteSurplusDialogOpen(false);
      setSurplusToDelete(null);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err });
    }
  };

  const businessDetails = business || null;
  const currency = businessDetails?.currency || "GHS";

  return (
    <div className="mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Surplus Sales</h1>
            <p className="text-sm text-muted-foreground">Record and manage additional revenue</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={handleAddSurplusSale}>
            <Plus className="mr-2 h-4 w-4" /> Add Surplus
          </Button>
        </div>
      </div>

      {/* Compact Filters Bar */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Shop Filter */}
          {viewableShops?.length > 0 && (
            <Select value={shopFilter} onValueChange={(val) => { setShopFilter(val === 'all' ? '' : val); setPage(1); }}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="All Shops" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shops</SelectItem>
                {viewableShops.map((shop) => (
                  <SelectItem key={shop.id} value={shop.id.toString()}>
                    {shop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* User Filter */}
          {canViewAllSales && (
            <Select value={userFilter} onValueChange={(val) => { setUserFilter(val === 'all' ? '' : val); setPage(1); }}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="All Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users?.map((user) => (
                  <SelectItem key={user.id} value={user.id.toString()}>
                    {user.username || `${user.first_name} ${user.last_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Date Range */}
          <div className="flex items-center gap-1 bg-muted/30 px-2 rounded-md border">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
            />
            <span className="text-muted-foreground text-xs">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
            />
          </div>

          {/* Quick Date Filters */}
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              const today = format(new Date(), "yyyy-MM-dd");
              setStartDate(today); setEndDate(today); setPage(1);
            }}>Today</Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              const today = new Date();
              const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
              setStartDate(format(weekAgo, "yyyy-MM-dd")); setEndDate(format(today, "yyyy-MM-dd")); setPage(1);
            }}>Week</Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              const today = new Date();
              const monthAgo = new Date(today); monthAgo.setMonth(today.getMonth() - 1);
              setStartDate(format(monthAgo, "yyyy-MM-dd")); setEndDate(format(today, "yyyy-MM-dd")); setPage(1);
            }}>Month</Button>
          </div>
        </div>
      </Card>

      {/* Compact Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Card className="p-2 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <p className="text-[10px] text-green-600 dark:text-green-400 font-medium">Total Surplus</p>
            <p className="text-lg font-bold text-green-900 dark:text-green-100">{currency} {numberWithCommas(parseFloat(summary.totalSurplus || 0).toFixed(2))}</p>
          </Card>
          <Card className="p-2 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <p className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">Total Records</p>
            <p className="text-lg font-bold text-blue-900 dark:text-blue-100">{numberWithCommas(summary.count)}</p>
          </Card>
          <Card className="p-2 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
            <p className="text-[10px] text-purple-600 dark:text-purple-400 font-medium">Average Surplus</p>
            <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
              {currency} {numberWithCommas(summary.count > 0 ? (parseFloat(summary.totalSurplus) / summary.count).toFixed(2) : '0.00')}
            </p>
          </Card>
        </div>
      )}

      {/* Surplus Sales Table */}
      <Card className="enterprise-table-container p-0">
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sales Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Recorded By</TableHead>
                <TableHead>Shop</TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && surplusSales?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center">
                    <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin" /> Loading surplus sales...</span>
                  </TableCell>
                </TableRow>
              ) : surplusSales?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                    No surplus sales found for this period.
                  </TableCell>
                </TableRow>
              ) : (
                surplusSales?.map((surplus) => (
                  <TableRow key={surplus.id}>
                    <TableCell className="font-medium">{format(new Date(surplus.sales_date), "dd MMM yyyy")}</TableCell>
                    <TableCell className="font-bold text-green-600">{currency} {numberWithCommas(parseFloat(surplus.amount).toFixed(2))}</TableCell>
                    <TableCell className="max-w-xs">
                      <div className="truncate" title={surplus.reason}>{surplus.reason}</div>
                    </TableCell>
                    <TableCell>{surplus.User?.username || 'N/A'}</TableCell>
                    <TableCell>{surplus.shop?.name || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{format(new Date(surplus.createdAt), "dd MMM yyyy HH:mm")}</TableCell>
                    <TableCell className="max-w-xs">
                      <div className="text-sm text-muted-foreground truncate" title={surplus.notes}>{surplus.notes || '-'}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditSurplusSale(surplus)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:text-red-700" onClick={() => handleDeleteSurplusClick(surplus)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-end space-x-2 py-4 px-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <div className="text-sm text-center min-w-[100px]">
            Page {page} of {pagination?.totalPages || 1}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= (pagination?.totalPages || 1) || loading}
          >
            Next
          </Button>
        </div>
      </Card>

      {/* Surplus Sales Dialog */}
      <SurplusSalesDialog
        open={surplusSalesDialogOpen}
        onOpenChange={setSurplusSalesDialogOpen}
        surplusSale={editingSurplusSale}
        onSuccess={() => {
          dispatch(fetchSurplusSales({ page, limit, startDate, endDate, shopId: shopFilter || undefined, userId: userFilter || undefined }));
        }}
      />

      {/* Delete Surplus Sale Confirmation Dialog */}
      <Dialog open={deleteSurplusDialogOpen} onOpenChange={setDeleteSurplusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Surplus Sale</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this surplus sale record of {currency} {surplusToDelete ? numberWithCommas(parseFloat(surplusToDelete.amount).toFixed(2)) : '0.00'}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSurplusDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDeleteSurplus} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
